import type { BucketedVariation } from '@convertcom/js-sdk'
import { parseLanguage, parseCountry } from '@/utils/parse'
import { getIsDevelopment } from '@/utils/app'

type StrippedVariation = Pick<BucketedVariation, 'experienceId' | 'experienceKey' | 'id' | 'key'>

type TrackOptions = {
  /**
   * Optional explicit user identifier to use for bucketing.
   * If not provided, the composable will try to derive it from auth or cookies,
   * and generate a random UUID as a last resort.
   */
  userId?: string
  /**
   * Optional explicit location key to use for Convert.
   * When omitted, it is derived from the current route path, mirroring the server logic.
   */
  location?: string
  /**
   * Additional properties to merge into the segment data.
   */
  properties?: Record<string, any>
  /**
   * Optional array of experience keys. For each key, pushCurrentVisitorExperimentToDataLayer
   * is called so the current visitor's experiment data is sent to the data layer.
   */
  experienceKeys?: string[]
}

const VISITOR_ID_COOKIE_NAME = 'ota-conv-context'

function removeTrailingSlash(path: string): string {
  if (!path) return path
  return path.endsWith('/') ? path.slice(0, -1) : path
}

function stripVariationProperties(variations: BucketedVariation[]): StrippedVariation[] {
  return variations.map((variation) => {
    const { experienceId, experienceKey, id, key } = variation
    return { experienceId, experienceKey, id, key }
  })
}

export const useConvert = () => {
  const nuxtApp = useNuxtApp()
  const route = useRoute()
  const runtimeConfig = useRuntimeConfig()
  const { pushCurrentVisitorExperimentToDataLayer } = useConvertExperiments()

  const visitorIdCookie = useCookie<string | null>(VISITOR_ID_COOKIE_NAME)
  const isDevelopment = getIsDevelopment(runtimeConfig.public)
  const environment = isDevelopment ? 'staging' : 'production'

  const track = async (options: TrackOptions = {}): Promise<StrippedVariation[] | null> => {
    const sdk = nuxtApp.$convertSdk

    if (!sdk) {
      return null
    }

    await sdk.onReady()

    let normalizedPathName = route.path

    if (normalizedPathName !== '/' && !normalizedPathName.endsWith('/')) {
      normalizedPathName = `${normalizedPathName}/`
    }

    if (!normalizedPathName.match(stripLanguageRegex)) {
      // Only do this for page routes that have a language code
      return null
    }

    const currentCountryLocaleParam = normalizedPathName.match(stripLanguageRegex)![0].replaceAll(/\//g, '')
    const locale = parseLanguage(currentCountryLocaleParam)
    const country = parseCountry(currentCountryLocaleParam)

    if (!locale || !country) {
      return null
    }

    if (!visitorIdCookie.value) {
      throw new Error('Visitor ID cookie is not found in the client')
    }

    const flatQueryParams: Record<string, any> = {}

    Object.entries(route.query).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        flatQueryParams[key] = value[0]
      } else if (value != null) {
        flatQueryParams[key] = value
      }
    })

    const segmentData = {
      country,
      locale,
      ...flatQueryParams,
      ...(options.properties || {})
    }

    const context = sdk.createContext(visitorIdCookie.value, segmentData)

    if (!context) {
      return null
    }

    const basePath = removeTrailingSlash(normalizedPathName)

    const currentLocation = options.location || basePath.replace(stripLanguageRegex, '').replaceAll(/\//g, '-').toLowerCase()

    const experienceKeys = options.experienceKeys ?? []

    if (!experienceKeys.length) {
      return null
    }

    const attributes = {
      locationProperties: { location: currentLocation, ...segmentData },
      environment,
      enableTracking: true
    }

    const variations = experienceKeys
      .map((key) => context.runExperience(key, attributes))
      .filter(
        (variation: unknown): variation is BucketedVariation =>
          typeof variation === 'object' && variation !== null && 'experienceKey' in (variation as BucketedVariation)
      )

    const strippedVariations = stripVariationProperties(variations)

    for (const experienceKey of experienceKeys) {
      pushCurrentVisitorExperimentToDataLayer(experienceKey)
    }

    return strippedVariations
  }

  const refreshConvertVariations = async () => {
    try {
      await $fetch('/nuxt-api/convert/refresh', {
        method: 'POST',
        body: {
          pathname: route.path
        }
      })
    } catch (error) {
      logError(error)
    }
  }

  return {
    track,
    refreshConvertVariations
  }
}
